import React, {useEffect, useState} from 'react';
import {NavBar} from 'Components/Nav/NavBar';
import Button from 'react-bootstrap/Button';
import s from './styles/Login.module.scss';
import {Link, useLocation} from 'react-router-dom'
import {capitalizeFLetter, push, validateAge, validateDate, validateName, validatePassword} from 'Utils/validation'

import {DayInput, FormErrors, SelectInput, TextInput, TypeAhead} from 'Input/Input'
import Form from 'react-bootstrap/Form'

import {collegeIds} from 'Utils/colleges'
import {Root} from 'Root'
import {toast, ToastContainer} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import styles from "../Discover/styles/University.module.scss";
import Modal from "react-bootstrap/Modal";
import Spinner from "react-bootstrap/Spinner";
import {getDeviceId, setSessionCookie} from "../../Utils/sessions";
import {logElementInteraction} from "../../Api/Utils/referral";

const gradYears = {
    2025: '2025',
    2026: '2026',
    2027: '2027',
    2028: '2028',
    2029: '2029',
    2030: '2030',
    2031: '2031',
    2032: '2032'
}


const Register = () => {
    const [isScrolled, setIsScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 50) {
                setIsScrolled(true);
            } else {
                setIsScrolled(false);
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    return <>
        <div className={`${s.navBarContainer} ${isScrolled ? s.navBarScrolled : ''}`}>
            <NavBar dark={true}/>
        </div>
        <RegisterForm/>
    </>
};

export default (Register);

const RegisterForm = () => {
    const [request, setRequest] = useState({})
    const [user, setUser] = useState(undefined)
    const [deviceId, setDeviceId] = useState(undefined)
    const [formErrors, setFormErrors] = useState({})
    const [registrationError, setRegistrationError] = useState(undefined)
    const [showMessagingSpeedBump, setShowMessagingSpeedBump] = useState(false)
    const [confirmPassword, setConfirmPassword] = useState('')
    const [loading, setLoading] = useState(false)
    const [loadingCurrent, setLoadingCurrent] = useState(false)
    const [loadingProspective, setLoadingProspective] = useState(false)
    const useQuery = () => {
        return new URLSearchParams(useLocation().search)
    }
    const reference = useQuery().get('reference')
    const partner = useQuery().get('partner')
    const studentType = useQuery().get('studentType')

    useEffect(() => {
        if (reference || partner) {
            setRequest(prevState => ({...prevState, 'referrer': reference, 'partner': partner}));
        }
    }, [partner, reference])

    useEffect(() => {
        if (studentType === 'CURRENT' || studentType === 'PROSPECTIVE') {
            setRequest(prevState => ({...prevState, studentType}))
        }
    }, [studentType])

    useEffect(() => {
        setDeviceId(getDeviceId());
    }, [])

    useEffect(() => {
        logElementInteraction({
            elementId: 'register::page',
            type: 'VIEW',
            deviceId: getDeviceId(),
            data: localStorage.getItem('userInfo'),
            source: 'WEB'
        }).catch(() => {})
    })

    useEffect(() => {
        if (user) {
            logElementInteraction({
                elementId: 'successful_registration::page',
                type: 'VIEW',
                deviceId: getDeviceId(),
                userExternalId: user.email,
                userInternalId: user.id,
                data: localStorage.getItem('userInfo'),
                source: 'WEB'
            }).catch(() => {})
        }
    }, [user])

    function submitStudentType(studentType) {
        setFormErrors({})
        if (studentType === 'CURRENT') {
            setLoadingCurrent(true);
        } else {
            setLoadingProspective(true);
        }
        setRequest(prevState => ({...prevState, studentType}))
        setLoadingCurrent(false);
        setLoadingProspective(false);
    }

    function completeRegistration() {
        setRegistrationError(undefined)
        setFormErrors({})
        const errors = {}

        setLoading(true)

        if (request.studentType !== 'CURRENT' && request.studentType !== 'PROSPECTIVE') {
            push(errors, 'studentType', "You must indicate whether you're in high school or college")
        }

        if (!validateName(request.firstName)) {
            push(errors, 'firstName', 'Valid first name is required ')
        }
        if (!validateName(request.lastName)) {
            push(errors, 'lastName', 'Valid last name is required ')
        }
        if (request.password && request.password !== confirmPassword) {
            push(errors, 'confirmPassword', 'Passwords do not match ')
        }
        if (!validatePassword(request.password)) {
            push(errors, 'password', 'Password must be at least 8 characters ')
        }

        if (!request.email) {
            if (request.studentType === 'CURRENT') {
                push(errors, 'email', 'A valid .edu email is required')
            } else {
                push(errors, 'email', 'A valid email is required')
            }
        } else if (request.studentType === 'CURRENT' && !request.email.endsWith('edu')) {
            push(errors, 'email', 'A valid .edu email is required')
        }

        if (!request.tos) {
            push(errors, 'tos', 'You must read and acknowledge the Terms and Conditions')
        }

        if (!validateDate(request.dateOfBirth)) {
            push(errors, 'dateOfBirth', 'Valid birthdate is required (MM/DD/YYYY) ')
        } else if (request.dateOfBirth && !validateAge(request.dateOfBirth)) {
            push(errors, 'dateOfBirth', 'You must be between the ages of 14 and 30 years to register ')
        }
        if (!request.graduationYear && request.studentType === 'CURRENT') {
            push(errors, 'graduationYear', 'Graduation Year is required ')
        }

        if (!request.college && request.studentType === 'CURRENT') {
            push(errors, 'college', 'University is required ')
        }

        Root.userApi.emailAvailable({email: request.email}).then(res => {
            if (res.status !== 200 || !res.data) {
                push(errors, 'email', 'Email is already taken ')
            }

            if (Object.keys(errors).length) {
                setFormErrors(errors)
                setLoading(false)
                return
            }

            ['firstName', 'lastName'].forEach(value => {
                const capital = capitalizeFLetter(request[value])
                setRequest(prevState => ({...prevState, [value]: capital}))
            })

            setRequest(prevState => ({...prevState, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone}))

            Root.userApi.registerBasicUser(request).then(res => {
                let registeredUser = res.data?.user;
                registeredUser.ambassadorData = undefined;
                registeredUser.jwt = res.data?.jwt;

                setSessionCookie(registeredUser);
                setUser(registeredUser);
                setLoading(false);
            }).catch(error => {
                setRegistrationError(error.response?.data ?? 'There was an issue creating your profile')
                setLoading(false)
            })
        }).catch(error => {
            push(errors, 'email', 'A valid email is required')
        })
    }

    function handleChange(event) {
        let {name, value} = event.target
        if (['email'].includes(name)) {
            value = value.trim()
        }
        if (name === 'confirmPassword') {
            setConfirmPassword(value)
        } else if (name === 'tos') {
            if (request.tos === 'true') {
                setRequest((prevState) => ({...prevState, ['tos']: null}));
            } else {
                setRequest((prevState) => ({...prevState, ['tos']: 'true'}));
            }
        } else {
            setRequest((prevState) => ({...prevState, [name]: value}));
        }
    }

    function handleChangeUni(event) {
        if (event.length > 0) {
            setRequest(prevState => ({...prevState, college: event[0].id}))
        }
    }

    function handleChangeDay(selectedDay, modifiers, dayPickerInput) {
        setRequest(prevState => ({...prevState, dateOfBirth: dayPickerInput.getInput().value}))
    }

    function continueLoggingIn() {
        logElementInteraction(
            {
                elementId: `register::slow_ack`,
                type: 'CLICK',
                deviceId: deviceId ?? 'anonymous',
                data: localStorage.getItem('userInfo'),
                userId: user?.id,
                source: 'WEB'
            }).catch((error) => {
                console.log(error)
        });
        window.location.href = '/login'
    }

    function pulseCheck() {
        Root.miscApi.serverPulseCheck()
            .then(() => {
            })
            .catch(() => {
                toast.error("It looks like the server is restarting, please refresh in a few seconds!");
            })
    }

    useEffect(() => {
        pulseCheck()
    }, [])

    return <div className={`${s.register}`}>
        <div className={s.form}>
            <h2>{user ? `Signup Successful!` : `Let's get you signed up!`}</h2>
            <div>
                {showMessagingSpeedBump &&
                    <Modal onHide={() => {
                        setShowMessagingSpeedBump(false);
                    }} show={showMessagingSpeedBump} className={styles.bookingModal}>
                        <Modal.Header closeButton>
                            <Modal.Title><strong>Receiving messages from us</strong></Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <p style={{fontSize: '16px', textAlign: 'center', marginTop: '10px'}}>By continuing, you
                                acknowledge
                                you
                                have read our <a href={'/privacy'} target={'_blank'} rel={"noreferrer"}>privacy
                                    policy</a> and
                                agree to receive SMS & MMS notifications from NXSTEP.
                                Message frequency varies. Msg. & Data Rates apply. You can opt-out of messages at
                                anytime in
                                the
                                settings
                                or by replying with &apos;STOP&apos;.</p>
                        </Modal.Body>

                        <Modal.Footer>
                            <Button className={'btnLink fs-16 fw-bold'} onClick={() => {
                                setShowMessagingSpeedBump(false);
                            }}>Continue</Button>
                        </Modal.Footer>
                    </Modal>
                }
                {user &&
                    <>
                        <p style={{textAlign: 'left'}}>
                            Your account has been created! We just sent you an email to verify and finalize everything.
                        </p>

                        {user.studentType === 'CURRENT' &&
                            <>
                                <hr></hr>
                                <h4 className={'mt-4'}>{`What's next?`}</h4>
                                <p style={{textAlign: 'left'}}>
                                    As most high schools are currently on summer break, we are currently in a state
                                    of preparation for their start in the fall. We will notify you of when that
                                    happens and when when high school students begin their search for mentors again;
                                    until then, we will send you periodic status updates to check in on you.
                                </p>
                            </>
                        }
                        <div className={"d-flex justify-content-center"}>
                            <Button className={`btnFill wide md`} onClick={() => continueLoggingIn()}>Continue Logging In</Button>
                        </div>
                    </>
                }
                {user === undefined && !showMessagingSpeedBump &&
                    <Form>
                        <Form.Group className="mb-4">
                            <p>First, tell me about yourself.</p>
                            <div style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                columnGap: '1rem',
                                marginBottom: '1rem'
                            }}>
                                <TextInput errors={formErrors} name={'firstName'} value={request.firstName}
                                           onChange={handleChange}
                                           placeholder={'First Name'}/>
                                <TextInput errors={formErrors} name={'lastName'} value={request.lastName}
                                           onChange={handleChange}
                                           placeholder={'Last Name'}/>
                            </div>
                            <DayInput containerClassName='mb-3' errors={formErrors} name={'dateOfBirth'}
                                      value={request.dateOfBirth}
                                      onChange={handleChangeDay} placeholder={'Birthdate (MM/DD/YYYY)'}/>
                        </Form.Group>
                        <Form.Group className="mb-4">
                            <p>Are you currently in high school or college?</p>
                            <div className={s.studentTypeChoiceContainer}>
                                <Button onClick={() => submitStudentType('PROSPECTIVE')} disabled={loadingProspective}
                                        className={`${request.studentType === 'PROSPECTIVE' ? `btnFill` : `btnOutline`} py-3`}>
                                    {loadingProspective ?
                                        <Spinner style={{color: "white"}} animation="border"
                                                 role="status"/> : `High School`}
                                </Button>
                                <Button onClick={() => submitStudentType('CURRENT')} disabled={loadingCurrent}
                                        className={`${request.studentType === 'CURRENT' ? `btnFill` : `btnOutline`} py-3`}
                                        style={{background: loadingCurrent ? "grey" : ""}}>
                                    {loadingCurrent ?
                                        <Spinner style={{color: "white"}} animation="border"
                                                 role="status"/> : `College`}
                                </Button>
                            </div>
                            <FormErrors errors={formErrors} name={'studentType'}/>
                            {request.studentType === 'CURRENT' &&
                                <div className={'mt-3'}>
                                    <SelectInput containerClassName='mb-3' id={'Graduation Year'}
                                                 placeholder={'Graduation Year'}
                                                 options={gradYears} errors={formErrors} name={'graduationYear'}
                                                 value={request.graduationYear} onChange={handleChange}/>
                                    <TypeAhead placeholder={'University'} options={collegeIds} containerClassName='mb-3'
                                               errors={formErrors} name={'college'} value={request.college}
                                               onChange={handleChangeUni}/>
                                    {/*<TextInput errors={formErrors} name={'referrer'} value={request.referrer} onChange={handleChange} placeholder={'Username or Email'} label="Referral (Leave blank if not applicable)"/>*/}
                                </div>
                            }
                        </Form.Group>

                        <Form.Group className="mb-1">
                            <p>{`Now, let's set your login credentials.`}</p>
                            <TextInput containerClassName='mb-3' errors={formErrors} name={'email'}
                                       value={request.email}
                                       onChange={handleChange} type={'email'}
                                       placeholder={`${request.studentType === 'CURRENT' ? 'Your college .edu email' : 'Email'}`}/>
                            <TextInput containerClassName='mb-3' type={'password'}
                                       placeholder={`Any password you'd like`}
                                       errors={formErrors}
                                       name={'password'} value={request.password} onChange={handleChange}/>
                            <TextInput containerClassName='mb-3' type={'password'} placeholder={'Confirm that password'}
                                       errors={formErrors}
                                       name={'confirmPassword'} value={confirmPassword} onChange={handleChange}/>
                        </Form.Group>

                        <Form.Group className="mb-4 mt-4">
                            <div>
                                <Link className='btnLink fs-14' target="_blank" to="/tos">Click to read the Terms of
                                    Service</Link>
                            </div>
                            <Form.Check inline name="tos" onChange={handleChange} value={'true'}
                                        checked={request.tos === 'true'}
                                        label="I have read, and I agree to the Terms and Conditions" type='checkbox'/>
                            <FormErrors errors={formErrors} name={'tos'}/>
                        </Form.Group>

                        <Form.Group className={"d-flex justify-content-center mt-3"}>
                            <Button onClick={completeRegistration} disabled={loading} className={`btnFill wide py-2`}
                                    style={{background: loading ? "grey" : ""}}>
                                {loading ?
                                    <Spinner style={{color: "white"}} animation="border" role="status"/> : `Submit`}
                            </Button>
                            <div className={"d-flex justify-content-around mt-1"}>
                                {registrationError && <p style={{color: 'red'}}>{registrationError}</p>}
                            </div>
                        </Form.Group>

                        <div className={"d-flex justify-content-center mt-3"}>
                            <Link className='btnLink fs-14' to="/login">Already have an account? Log In</Link>
                        </div>
                    </Form>
                }
                <ToastContainer theme="dark" autoClose={false} position="bottom-center"/>
            </div>
        </div>
    </div>
};


export const EmailVerificationSuccess = () => {
    return <>
        <div className={`${s.navBarContainer}`}>
            <NavBar dark={true}/>
        </div>
        <div className={s.status}>
            <div className={`${s.form}`}>
                <h2>{`Email Verification Successful`}</h2>
                <div>
                    <p style={{textAlign: 'center'}}>
                        Thanks for verifying! This is just one of the tools we use to help make NXSTEP more safe and
                        secure.
                    </p>
                    <div className={"d-flex justify-content-center"}>
                        <Button className={`btnFill wide md`} href="/login">Log In</Button>
                    </div>
                    <ToastContainer theme="dark" autoClose={false} position="bottom-center"/>
                </div>
            </div>
        </div>
    </>
}

export const EmailVerificationFailure = () => {
    const useQuery = () => {
        return new URLSearchParams(useLocation().search)
    }

    const reason = useQuery().get('reason')

    return <>
        <div className={`${s.navBarContainer}`}>
            <NavBar dark={true}/>
        </div>
        <div className={s.status}>
            <div className={`${s.form}`}>
                <h2>{`Unable to verify your email`}</h2>
                <div>
                    {reason === '404' &&
                        <p style={{textAlign: 'center'}}>An account could not be found with the associated email</p>
                    }
                    {reason === '208' &&
                        <p style={{textAlign: 'center'}}>The link has expired and can no longer be used.</p>
                    }
                    {reason !== '208' && reason !== '404' &&
                        <p style={{textAlign: 'center'}}>{`We're not sure what went wrong :(`}</p>
                    }
                    <div className={"d-flex justify-content-center"}>
                        <Button className={`btnFill wide md`} href="/register">Sign Up</Button>
                    </div>
                    <ToastContainer theme="dark" autoClose={false} position="bottom-center"/>
                </div>
            </div>
        </div>
    </>
}